import React, { useEffect, useState } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Sparkles,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Edit3,
  Check,
  HelpCircle,
  CheckCheck,
  Loader2,
  X,
  Info,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import CustomSelect, { SelectOption } from "../components/CustomSelect";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import { formatToLocal12HourDateString } from "../../../../app/shared/utils/dateUtils";
import "../styles/QuestionBankStyles.css";

const difficultyOptions: SelectOption[] = [
  { value: "Easy", label: "Easy" },
  { value: "Medium", label: "Medium" },
  { value: "Hard", label: "Hard" },
];

interface GeneratedQuestionItem {
  question_id: string;
  question: string;
  question_type: string;
  difficulty_level: string;
  module_id?: string;
  module_name?: string;
  explanation?: string;
  status: string; // "Pending", "Approved", "Rejected"
  concepts?: string[];
  answers: { answer_id: string; answer: string; correct_answer: boolean }[];
}

interface RequestDetails {
  request_id: string;
  module_id: string;
  module_name?: string;
  request_datetime?: string;
  request_status: string;
  no_of_questions_requested?: number;
  no_of_questions_generated?: number;
  no_of_question_approved?: number;
  no_of_question_pending?: number;
  no_of_question_rejected?: number;
}

const AIQuestionReviewPage: React.FC = () => {
  const location = useLocation();
  const params = useParams<{ requestId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const requestId = location.state?.requestId || params.requestId || "";
  const moduleId =
    location.state?.moduleId || searchParams.get("module_id") || "";

  const [requestDetails, setRequestDetails] = useState<RequestDetails | null>(
    null,
  );
  const [questions, setQuestions] = useState<GeneratedQuestionItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<
    "pending" | "approved" | "rejected"
  >("pending");
  const [selectedQId, setSelectedQId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [approveAllLoading, setApproveAllLoading] = useState<boolean>(false);
  const [rejectAllLoading, setRejectAllLoading] = useState<boolean>(false);

  const [showInfoTooltip, setShowInfoTooltip] = useState<boolean>(false);

  // Edit State
  const [editingQId, setEditingQId] = useState<string | null>(null);
  const [editQText, setEditQText] = useState<string>("");
  const [editExplanation, setEditExplanation] = useState<string>("");
  const [editDifficulty, setEditDifficulty] = useState<string>("Easy");
  const [editConcepts, setEditConcepts] = useState<string>("");
  const [editOptions, setEditOptions] = useState<
    { answer_id: string; answer: string; correct_answer: boolean }[]
  >([]);

  const formatRequestDate = (dtStr?: string) => {
    return formatToLocal12HourDateString(dtStr);
  };

  useEffect(() => {
    if (requestId && moduleId) {
      fetchData();
    }
  }, [requestId, moduleId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await QuestionBankClient.getAIGenerationRequestDetails(
        requestId!,
        moduleId,
      );
      if (res) {
        setRequestDetails(res.request);
        const qList: GeneratedQuestionItem[] = res.questions || [];
        setQuestions(qList);
        if (qList.length > 0 && !selectedQId) {
          const firstPending = qList.find(
            (q) => q.status !== "Approved" && q.status !== "Rejected",
          );
          setSelectedQId(
            firstPending ? firstPending.question_id : qList[0].question_id,
          );
        }
      }
    } catch (err) {
      console.error("Error fetching AI question details:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = questions.filter((q) => {
    if (activeFilter === "pending")
      return q.status !== "Approved" && q.status !== "Rejected";
    if (activeFilter === "approved") return q.status === "Approved";
    if (activeFilter === "rejected") return q.status === "Rejected";
    return true;
  });

  const currentSelectedQ =
    filteredQuestions.find((q) => q.question_id === selectedQId) ||
    filteredQuestions[0] ||
    questions[0] ||
    null;

  const handleStatusChange = async (
    questionId: string,
    status: "Approved" | "Rejected",
  ) => {
    try {
      setActionLoadingId(questionId);
      await QuestionBankClient.updateAIGeneratedQuestionStatus({
        module_id: moduleId,
        request_id: requestId!,
        question_id: questionId,
        status: status,
      });

      // Update local state smoothly
      setQuestions((prev) =>
        prev.map((q) => (q.question_id === questionId ? { ...q, status } : q)),
      );

      // Auto-advance selection to next pending question if any
      const remainingPending = questions.filter(
        (q) =>
          q.question_id !== questionId &&
          q.status !== "Approved" &&
          q.status !== "Rejected",
      );
      if (remainingPending.length > 0) {
        setSelectedQId(remainingPending[0].question_id);
      }

      setActionLoadingId(null);
    } catch (err) {
      console.error(`Error marking question ${status}:`, err);
      setActionLoadingId(null);
    }
  };

  const handleApproveAll = async () => {
    try {
      setApproveAllLoading(true);
      await QuestionBankClient.approveAllAIGeneratedQuestions(
        requestId!,
        moduleId,
      );
      fetchData();
    } catch (err) {
      console.error("Error approving all questions:", err);
    } finally {
      setApproveAllLoading(false);
    }
  };

  const handleRejectAll = async () => {
    try {
      setRejectAllLoading(true);
      await QuestionBankClient.rejectAllAIGeneratedQuestions(
        requestId!,
        moduleId,
      );
      fetchData();
    } catch (err) {
      console.error("Error rejecting all questions:", err);
    } finally {
      setRejectAllLoading(false);
    }
  };

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: "Confirmation",
    message: "",
    onConfirm: () => {},
  });

  const confirmRejectSingle = (questionId: string) => {
    setConfirmModal({
      show: true,
      title: "Confirmation",
      message: "Are you sure you want to reject this AI-generated question?",
      onConfirm: () => handleStatusChange(questionId, "Rejected"),
    });
  };

  const confirmApproveSingle = (questionId: string) => {
    setConfirmModal({
      show: true,
      title: "Confirmation",
      message: "Are you sure you want to approve this AI-generated question?",
      onConfirm: () => handleStatusChange(questionId, "Approved"),
    });
  };

  const confirmRejectAll = () => {
    setConfirmModal({
      show: true,
      title: "Confirmation",
      message:
        "Are you sure you want to reject all the AI-generated questions?",
      onConfirm: () => handleRejectAll(),
    });
  };

  const confirmApproveAll = () => {
    setConfirmModal({
      show: true,
      title: "Confirmation",
      message:
        "Are you sure you want to approve all the AI-generated questions?",
      onConfirm: () => handleApproveAll(),
    });
  };

  const startEdit = (q: GeneratedQuestionItem) => {
    setEditingQId(q.question_id);
    setEditQText(q.question);
    setEditExplanation(q.explanation || "");
    setEditDifficulty(q.difficulty_level || "Easy");
    setEditConcepts(
      Array.isArray(q.concepts)
        ? q.concepts.join(", ")
        : typeof q.concepts === "string"
          ? q.concepts
          : "",
    );
    setEditOptions(q.answers ? [...q.answers] : []);
  };

  const saveEdit = async (questionId: string) => {
    try {
      setActionLoadingId(questionId);
      const parsedConcepts = editConcepts
        ? editConcepts
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean)
        : [];

      await QuestionBankClient.editAIGeneratedQuestion(
        questionId,
        requestId,
        moduleId,
        {
          question: editQText,
          explanation: editExplanation,
          difficulty_level: editDifficulty,
          concepts: parsedConcepts,
          answers: editOptions,
        },
      );
      const updatedList = questions.map((q) => {
        if (q.question_id === questionId) {
          return {
            ...q,
            question: editQText,
            explanation: editExplanation,
            difficulty_level: editDifficulty,
            concepts: parsedConcepts,
            answers: editOptions,
          };
        }
        return q;
      });
      setQuestions(updatedList);
      setEditingQId(null);
      setActionLoadingId(null);
    } catch (err) {
      console.error("Error saving edits:", err);
      setActionLoadingId(null);
    }
  };

  const totalCount = questions.length;
  const approvedCount = questions.filter((q) => q.status === "Approved").length;
  const rejectedCount = questions.filter((q) => q.status === "Rejected").length;
  const pendingCount = totalCount - approvedCount - rejectedCount;

  const isFailedRequest =
    requestDetails?.request_status?.toLowerCase().includes("fail") ||
    requestDetails?.request_status?.toLowerCase().includes("err");

  const isActionLoading =
    loading || approveAllLoading || rejectAllLoading || !!actionLoadingId;

  const loaderMessage = approveAllLoading
    ? "Approving all questions..."
    : rejectAllLoading
      ? "Rejecting all questions..."
      : actionLoadingId
        ? "Updating question status..."
        : "Loading AI Generated Questions...";

  return (
    <div className="qb-review-workspace">
      <Loader isLoading={isActionLoading} message={loaderMessage} />

      {/* Top Header Bar */}
      <div className="qb-review-top-bar">
        <div className="qb-review-top-row">
          <div className="qb-review-top-left">
            <h2 className="qb-review-module-title">
              {requestDetails?.module_name || "AI Generated Questions"}
            </h2>
            <div
              className="qb-info-wrapper"
              onMouseEnter={() => setShowInfoTooltip(true)}
              onMouseLeave={() => setShowInfoTooltip(false)}
            >
              <button type="button" className="qb-info-icon-btn">
                <Info size={18} />
              </button>

              {showInfoTooltip && (
                <div className="qb-mui-tooltip">
                  <div className="qb-tooltip-grid">
                    <span className="qb-tooltip-label">Context Type</span>
                    <span className="qb-tooltip-value">Text</span>

                    <span className="qb-tooltip-label">Questions</span>
                    <span className="qb-tooltip-value">
                      {requestDetails?.no_of_questions_requested ||
                        requestDetails?.no_of_questions_generated ||
                        totalCount ||
                        0}
                    </span>

                    <span className="qb-tooltip-label">Request Date</span>
                    <span className="qb-tooltip-value">
                      {formatRequestDate(requestDetails?.request_datetime)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <span
              className="qb-review-status-pill"
              style={
                isFailedRequest
                  ? {
                      backgroundColor: "#fef2f2",
                      color: "#dc2626",
                      border: "1px solid #fecaca",
                      fontWeight: 600,
                    }
                  : undefined
              }
            >
              {requestDetails?.request_status || "In-review"}
            </span>
          </div>

          <div className="qb-review-top-right">
            <div className="qb-review-count-badge">
              Generated Questions <strong>{totalCount}</strong>
            </div>

            <button
              className="qb-review-close-btn"
              title="Close Workspace"
              onClick={() => navigate("/dashboard/question-bank/ai-requests")}
            >
              <X size={16} />
            </button>

            {pendingCount > 0 && (
              <>
                <button
                  type="button"
                  className="qb-review-reject-btn"
                  disabled={rejectAllLoading || approveAllLoading}
                  onClick={confirmRejectAll}
                  style={{ padding: "8px 16px", fontSize: "13px" }}
                >
                  <XCircle size={15} />
                  {rejectAllLoading ? "Rejecting..." : "Reject all"}
                </button>
                <button
                  type="button"
                  className="qb-review-approve-btn"
                  disabled={approveAllLoading || rejectAllLoading}
                  onClick={confirmApproveAll}
                  style={{ padding: "8px 18px", fontSize: "13px" }}
                >
                  <CheckCheck size={16} />
                  {approveAllLoading ? "Approving..." : "Approve all"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filter Tabs matching reference UI */}
        <div className="qb-review-tabs-row">
          <div
            className={`qb-review-tab-item ${activeFilter === "pending" ? "active" : ""}`}
            onClick={() => {
              setActiveFilter("pending");
              const firstP = questions.find(
                (q) => q.status !== "Approved" && q.status !== "Rejected",
              );
              if (firstP) setSelectedQId(firstP.question_id);
            }}
          >
            Pending ({pendingCount})
          </div>
          <div
            className={`qb-review-tab-item ${activeFilter === "approved" ? "active" : ""}`}
            onClick={() => {
              setActiveFilter("approved");
              const firstA = questions.find((q) => q.status === "Approved");
              if (firstA) setSelectedQId(firstA.question_id);
            }}
          >
            Approved ({approvedCount})
          </div>
          <div
            className={`qb-review-tab-item ${activeFilter === "rejected" ? "active" : ""}`}
            onClick={() => {
              setActiveFilter("rejected");
              const firstR = questions.find((q) => q.status === "Rejected");
              if (firstR) setSelectedQId(firstR.question_id);
            }}
          >
            Rejected ({rejectedCount})
          </div>
        </div>
      </div>

      {/* 2-Panel Split Body */}
      {isFailedRequest ? (
        <div
          className="qb-table-card"
          style={{
            padding: "48px 24px",
            textAlign: "center",
            color: "#64748b",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#ffffff",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <XCircle size={34} color="#dc2626" />
          </div>
          <h3
            style={{
              margin: "0 0 8px",
              fontSize: "18px",
              fontWeight: "700",
              color: "#991b1b",
              textAlign: "center",
            }}
          >
            AI Question Generation Failed
          </h3>
          <p
            style={{
              margin: "0 0 24px",
              fontSize: "14px",
              color: "#64748b",
              maxWidth: "480px",
              lineHeight: "1.5",
              textAlign: "center",
            }}
          >
            This question generation request encountered an error or failed to
            generate questions. You can attempt to generate questions again for
            this module.
          </p>
          {/* <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              className="qb-btn qb-btn-secondary"
              onClick={() => navigate("/dashboard/question-bank/ai-requests")}
            >
              <ArrowLeft size={16} /> Back to Generation Requests
            </button>
            <button
              type="button"
              className="qb-btn qb-btn-primary"
              onClick={() => navigate("/dashboard/question-bank/generate-ai")}
            >
              <Sparkles size={16} /> Try Generating Again
            </button>
          </div> */}
        </div>
      ) : filteredQuestions.length === 0 ? (
        <div
          className="qb-table-card"
          style={{
            padding: "48px 24px",
            textAlign: "center",
            color: "#64748b",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "12px",
            }}
          >
            <HelpCircle size={32} color="#94a3b8" />
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "700",
              color: "#1e293b",
              textAlign: "center",
            }}
          >
            No Questions Found in this Category
          </h3>
        </div>
      ) : (
        <div className="qb-review-split-body">
          {/* Left Panel: Question List */}
          <div className="qb-review-left-panel">
            <div className="qb-review-qlist-scroll">
              {filteredQuestions.map((q) => {
                const isSelected =
                  currentSelectedQ?.question_id === q.question_id;
                const isRejected = q.status === "Rejected";
                const diffClass =
                  q.difficulty_level === "Easy"
                    ? "qb-badge-easy"
                    : q.difficulty_level === "Hard"
                      ? "qb-badge-hard"
                      : "qb-badge-medium";

                return (
                  <div
                    key={q.question_id}
                    className={`qb-review-qcard-item ${isSelected ? "selected" : ""} ${isRejected ? "rejected" : ""}`}
                    onClick={() => {
                      setSelectedQId(q.question_id);
                      setEditingQId(null);
                    }}
                  >
                    <div className="qb-review-qcard-text">{q.question}</div>
                    <span className={`qb-review-qcard-diff ${diffClass}`}>
                      {q.difficulty_level || "Easy"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Detailed View & Action Toolbar */}
          {currentSelectedQ && (
            <div className="qb-review-right-panel">
              <div className="qb-review-main-content">
                {editingQId === currentSelectedQ.question_id ? (
                  /* Editable Mode */
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#07473b",
                          display: "block",
                          marginBottom: "6px",
                        }}
                      >
                        Question Text:
                      </label>
                      <textarea
                        className="qb-textarea"
                        rows={3}
                        value={editQText}
                        onChange={(e) => setEditQText(e.target.value)}
                      />
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 2fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#07473b",
                            display: "block",
                            marginBottom: "6px",
                          }}
                        >
                          Difficulty Level:
                        </label>
                        <CustomSelect
                          options={difficultyOptions}
                          value={editDifficulty}
                          onChange={(val) => setEditDifficulty(val)}
                          placeholder="Select Difficulty"
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#07473b",
                            display: "block",
                            marginBottom: "6px",
                          }}
                        >
                          Concepts:
                        </label>
                        <input
                          type="text"
                          className="qb-input"
                          placeholder="Concept-1, Concept-2"
                          value={editConcepts}
                          onChange={(e) => setEditConcepts(e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#07473b",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        Response options:
                      </label>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {editOptions.map((opt, optIdx) => (
                          <div
                            key={opt.answer_id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <input
                              type="radio"
                              name={`correct_radio_${currentSelectedQ.question_id}`}
                              checked={opt.correct_answer}
                              onChange={() => {
                                const updated = editOptions.map((o, idx) => ({
                                  ...o,
                                  correct_answer: idx === optIdx,
                                }));
                                setEditOptions(updated);
                              }}
                              style={{
                                width: "18px",
                                height: "18px",
                                accentColor: "#0d6e5b",
                              }}
                            />
                            <span
                              style={{
                                fontWeight: 700,
                                width: "24px",
                                color: "#475569",
                              }}
                            >
                              {String.fromCharCode(65 + optIdx)}:
                            </span>
                            <input
                              type="text"
                              className="qb-input"
                              value={opt.answer}
                              onChange={(e) => {
                                const updated = [...editOptions];
                                updated[optIdx].answer = e.target.value;
                                setEditOptions(updated);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#07473b",
                          display: "block",
                          marginBottom: "6px",
                        }}
                      >
                        Explanation (Optional):
                      </label>
                      <textarea
                        className="qb-textarea"
                        rows={3}
                        value={editExplanation}
                        onChange={(e) => setEditExplanation(e.target.value)}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "flex-end",
                        marginTop: "12px",
                      }}
                    >
                      <button
                        type="button"
                        className="qb-btn qb-btn-outline"
                        onClick={() => setEditingQId(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="qb-btn qb-btn-primary"
                        onClick={() => saveEdit(currentSelectedQ.question_id)}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Read Review View matching Institute Reference UI in InterviewIQ dark green */
                  <>
                    {/* Top Question Box */}
                    <div
                      className={`qb-review-qbox ${currentSelectedQ.status === "Rejected" ? "rejected" : ""}`}
                    >
                      {currentSelectedQ.question}
                    </div>

                    {/* Options Stack */}
                    <div className="qb-review-options-stack">
                      {currentSelectedQ.answers &&
                        currentSelectedQ.answers.map((opt, optIdx) => {
                          const isCorrect = opt.correct_answer;
                          const letterBadge = String.fromCharCode(65 + optIdx);

                          return (
                            <div
                              key={opt.answer_id || optIdx}
                              className={`qb-review-option-pill ${isCorrect ? "correct-choice" : ""}`}
                            >
                              <div className="qb-review-option-badge">
                                {letterBadge}
                              </div>
                              <div className="qb-review-option-text">
                                {opt.answer}
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* Concepts Section */}
                    {((Array.isArray(currentSelectedQ.concepts) &&
                      currentSelectedQ.concepts.length > 0) ||
                      (typeof currentSelectedQ.concepts === "string" &&
                        (currentSelectedQ.concepts as string).trim() !==
                          "")) && (
                      <div
                        style={{
                          background: "#ffffff",
                          border: "1px solid #dde5e2",
                          borderRadius: "10px",
                          padding: "12px 16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#07473b",
                            marginBottom: "8px",
                            letterSpacing: "0.5px",
                          }}
                        >
                          CONCEPTS
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {(Array.isArray(currentSelectedQ.concepts)
                            ? currentSelectedQ.concepts
                            : (currentSelectedQ.concepts as string).split(",")
                          )
                            .map((c) => c.trim())
                            .filter(Boolean)
                            .map((concept, idx) => (
                              <span
                                key={idx}
                                style={{
                                  background: "#e6f4f1",
                                  color: "#07473b",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  padding: "4px 12px",
                                  borderRadius: "16px",
                                  border: "1px solid #bfe3db",
                                }}
                              >
                                {concept}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Explanation Box */}
                    {currentSelectedQ.explanation && (
                      <div className="qb-review-explanation-box">
                        <div className="qb-review-explanation-title">
                          EXPLANATION
                        </div>
                        <p className="qb-review-explanation-text">
                          {currentSelectedQ.explanation}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Bottom Action Toolbar */}
              {editingQId !== currentSelectedQ.question_id &&
                currentSelectedQ.status !== "Rejected" && (
                  <div className="qb-review-action-footer">
                    {currentSelectedQ.status === "Approved" ? (
                      /* Approved Questions: Show ONLY Edit button */
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          width: "100%",
                        }}
                      >
                        <button
                          type="button"
                          className="qb-btn qb-btn-outline"
                          onClick={() => startEdit(currentSelectedQ)}
                        >
                          <Edit3 size={15} /> Edit
                        </button>
                      </div>
                    ) : (
                      /* Pending AI Review Questions: Show Rephrase, Edit, Reject, Approve */
                      <>
                        <button
                          type="button"
                          className="qb-review-rephrase-btn"
                          onClick={() =>
                            navigate("/dashboard/question-bank/ai-rephrase", {
                              state: {
                                questionId: currentSelectedQ.question_id,
                                requestId,
                                moduleId,
                              },
                            })
                          }
                        >
                          <Sparkles size={16} /> Rephrase
                        </button>

                        <div className="qb-review-btn-group">
                          <button
                            type="button"
                            className="qb-btn qb-btn-outline"
                            onClick={() => startEdit(currentSelectedQ)}
                          >
                            <Edit3 size={15} /> Edit
                          </button>

                          <button
                            type="button"
                            className="qb-review-reject-btn"
                            disabled={
                              actionLoadingId === currentSelectedQ.question_id
                            }
                            onClick={() =>
                              confirmRejectSingle(currentSelectedQ.question_id)
                            }
                          >
                            <XCircle size={16} /> Reject
                          </button>

                          <button
                            type="button"
                            className="qb-review-approve-btn"
                            disabled={
                              actionLoadingId === currentSelectedQ.question_id
                            }
                            onClick={() =>
                              confirmApproveSingle(currentSelectedQ.question_id)
                            }
                          >
                            <Check size={16} /> Approve
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      <Alert
        show={confirmModal.show}
        type="confirm"
        title={confirmModal.title}
        message={confirmModal.message}
        primaryButtonText="Yes"
        secondaryButtonText="No"
        onPrimaryClick={() => {
          const action = confirmModal.onConfirm;
          setConfirmModal((prev) => ({ ...prev, show: false }));
          if (action) action();
        }}
        onSecondaryClick={() => {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }}
        onClose={() => {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }}
      />
    </div>
  );
};

export default AIQuestionReviewPage;
